import React, { useState } from 'react';
import Layout from '../../components/Layout';
import classes from './Learn.module.css';

const Learn = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Sample learning content
    const learningCategories = [
        { id: 'all', label: 'All Topics', count: 24 },
        { id: 'basics', label: 'Financial Basics', count: 8 },
        { id: 'investing', label: 'Investing', count: 6 },
        { id: 'budgeting', label: 'Budgeting', count: 5 },
        { id: 'retirement', label: 'Retirement', count: 3 },
        { id: 'debt', label: 'Debt Management', count: 2 }
    ];

    const featuredCourses = [
        {
            id: 1,
            title: 'Personal Finance Fundamentals',
            description: 'Master the basics of personal finance, budgeting, and financial planning.',
            duration: '2 hours',
            level: 'Beginner',
            progress: 0,
            thumbnail: '💰',
            category: 'basics'
        },
        {
            id: 2,
            title: 'Investment Portfolio Building',
            description: 'Learn how to build and manage a diversified investment portfolio.',
            duration: '3 hours',
            level: 'Intermediate',
            progress: 65,
            thumbnail: '📈',
            category: 'investing'
        },
        {
            id: 3,
            title: 'Advanced Budgeting Strategies',
            description: 'Discover advanced techniques for effective budgeting and expense tracking.',
            duration: '1.5 hours',
            level: 'Intermediate',
            progress: 30,
            thumbnail: '📊',
            category: 'budgeting'
        }
    ];

    const articles = [
        {
            id: 1,
            title: '10 Essential Financial Habits for Young Adults',
            excerpt: 'Building good financial habits early can set you up for lifelong success.',
            readTime: '5 min read',
            category: 'basics',
            publishedAt: '2 days ago',
            author: 'Sarah Johnson'
        },
        {
            id: 2,
            title: 'Understanding Market Volatility: A Beginners Guide',
            excerpt: 'Learn how to navigate market ups and downs without panic.',
            readTime: '7 min read',
            category: 'investing',
            publishedAt: '1 week ago',
            author: 'Michael Chen'
        },
        {
            id: 3,
            title: 'The 50/30/20 Rule: Simplified Budgeting',
            excerpt: 'A simple budgeting framework that actually works for real people.',
            readTime: '4 min read',
            category: 'budgeting',
            publishedAt: '3 days ago',
            author: 'Emily Rodriguez'
        },
        {
            id: 4,
            title: 'Retirement Planning in Your 30s',
            excerpt: 'Its never too early to start planning for retirement.',
            readTime: '6 min read',
            category: 'retirement',
            publishedAt: '5 days ago',
            author: 'David Kim'
        }
    ];

    const videos = [
        {
            id: 1,
            title: 'Emergency Fund 101',
            duration: '8:45',
            views: '12.5K',
            category: 'basics',
            thumbnail: '🏦'
        },
        {
            id: 2,
            title: 'Dollar-Cost Averaging Explained',
            duration: '6:23',
            views: '8.2K',
            category: 'investing',
            thumbnail: '💹'
        },
        {
            id: 3,
            title: 'Debt Snowball vs Avalanche',
            duration: '10:12',
            views: '15.7K',
            category: 'debt',
            thumbnail: '❄️'
        }
    ];

    const tools = [
        {
            id: 1,
            name: 'Budget Calculator',
            description: 'Plan your monthly budget with our interactive calculator',
            icon: '🧮',
            type: 'calculator'
        },
        {
            id: 2,
            name: 'Investment Simulator',
            description: 'See how your investments could grow over time',
            icon: '📊',
            type: 'simulator'
        },
        {
            id: 3,
            name: 'Debt Payoff Planner',
            description: 'Create a strategy to pay off your debts faster',
            icon: '🎯',
            type: 'planner'
        },
        {
            id: 4,
            name: 'Retirement Calculator',
            description: 'Calculate how much you need to save for retirement',
            icon: '🏖️',
            type: 'calculator'
        }
    ];

    const filteredContent = (content, category) => {
        if (activeCategory === 'all') return content;
        return content.filter(item => item.category === activeCategory);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
    };

    const handleStartCourse = (courseId) => {
        console.log(`Starting course: ${courseId}`);
    };

    const handleReadArticle = (articleId) => {
        console.log(`Reading article: ${articleId}`);
    };

    const handleWatchVideo = (videoId) => {
        console.log(`Watching video: ${videoId}`);
    };

    const handleUseTool = (toolId) => {
        console.log(`Using tool: ${toolId}`);
    };

    return (
        <Layout>
            <div className={classes.learn}>
                {/* Hero Header */}
                <div className={classes.heroHeader}>
                    <div className={classes.heroContent}>
                        <div className={classes.heroText}>
                            <h1 className={classes.heroTitle}>Financial Education Hub</h1>
                            <p className={classes.heroSubtitle}>
                                Master your money with expert-curated courses, articles, and interactive tools
                            </p>

                            {/* Search Bar */}
                            <div className={classes.searchWrapper}>
                                <div className={classes.searchIcon}>🔍</div>
                                <input
                                    type="text"
                                    placeholder="Search courses, articles, videos..."
                                    className={classes.searchInput}
                                    value={searchQuery}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div>

                        {/* Learning Stats */}
                        <div className={classes.learningStats}>
                            <h3 className={classes.statsTitle}>Your Learning Journey</h3>
                            <div className={classes.statsGrid}>
                                <div className={classes.statItem}>
                                    <div className={classes.statValue}>3</div>
                                    <div className={classes.statLabel}>Courses Completed</div>
                                </div>
                                <div className={classes.statItem}>
                                    <div className={classes.statValue}>12</div>
                                    <div className={classes.statLabel}>Articles Read</div>
                                </div>
                                <div className={classes.statItem}>
                                    <div className={classes.statValue}>8h</div>
                                    <div className={classes.statLabel}>Study Time</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.heroDecorations}></div>
                </div>

                <div className={classes.mainContent}>
                    {/* Category Filters */}
                    <div className={classes.filtersSection}>
                        <div className={classes.premiumCard}>
                            <h3 className={classes.filtersTitle}>Browse by Category</h3>
                            <div className={classes.categoryTabs}>
                                {learningCategories.map(category => (
                                    <button
                                        key={category.id}
                                        onClick={() => handleCategoryChange(category.id)}
                                        className={`${classes.categoryTab} ${activeCategory === category.id ? classes.categoryTabActive : ''}`}
                                    >
                                        <span className={classes.categoryLabel}>{category.label}</span>
                                        <span className={classes.categoryCount}>({category.count})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Featured Courses */}
                    <div className={classes.coursesSection}>
                        <div className={classes.sectionHeader}>
                            <h2 className={classes.sectionTitle}>Featured Courses</h2>
                            <p className={classes.sectionSubtitle}>Comprehensive learning paths to advance your financial knowledge</p>
                        </div>

                        <div className={classes.coursesGrid}>
                            {filteredContent(featuredCourses, activeCategory).map(course => (
                                <div key={course.id} className={classes.courseCard}>
                                    <div className={classes.courseThumbnail}>
                                        <div className={classes.courseIcon}>{course.thumbnail}</div>
                                        <div className={classes.courseLevel}>{course.level}</div>
                                    </div>

                                    <div className={classes.courseContent}>
                                        <h3 className={classes.courseTitle}>{course.title}</h3>
                                        <p className={classes.courseDescription}>{course.description}</p>

                                        <div className={classes.courseMeta}>
                                            <span className={classes.courseDuration}>⏱️ {course.duration}</span>
                                            <span className={classes.courseCategory}>{learningCategories.find(c => c.id === course.category)?.label}</span>
                                        </div>

                                        {course.progress > 0 && (
                                            <div className={classes.progressSection}>
                                                <div className={classes.progressBar}>
                                                    <div
                                                        className={classes.progressFill}
                                                        style={{ width: `${course.progress}%` }}
                                                    ></div>
                                                </div>
                                                <span className={classes.progressText}>{course.progress}% Complete</span>
                                            </div>
                                        )}

                                        <button
                                            className={classes.courseButton}
                                            onClick={() => handleStartCourse(course.id)}
                                        >
                                            {course.progress > 0 ? 'Continue Course' : 'Start Course'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className={classes.contentGrid}>
                        {/* Articles Section */}
                        <div className={classes.articlesSection}>
                            <div className={classes.premiumCard}>
                                <div className={classes.cardHeader}>
                                    <h2 className={classes.cardTitle}>Latest Articles</h2>
                                    <button className={classes.viewAllBtn}>View All →</button>
                                </div>

                                <div className={classes.articlesList}>
                                    {filteredContent(articles, activeCategory).map(article => (
                                        <div key={article.id} className={classes.articleItem}>
                                            <div className={classes.articleContent}>
                                                <h4 className={classes.articleTitle}>{article.title}</h4>
                                                <p className={classes.articleExcerpt}>{article.excerpt}</p>
                                                <div className={classes.articleMeta}>
                                                    <span className={classes.articleAuthor}>By {article.author}</span>
                                                    <span className={classes.articleSeparator}>•</span>
                                                    <span className={classes.articleReadTime}>{article.readTime}</span>
                                                    <span className={classes.articleSeparator}>•</span>
                                                    <span className={classes.articleDate}>{article.publishedAt}</span>
                                                </div>
                                            </div>
                                            <button
                                                className={classes.readBtn}
                                                onClick={() => handleReadArticle(article.id)}
                                            >
                                                Read
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Videos & Tools Sidebar */}
                        <div className={classes.sidebarContent}>
                            {/* Videos Section */}
                            <div className={classes.videosSection}>
                                <div className={classes.premiumCard}>
                                    <h3 className={classes.cardTitle}>Popular Videos</h3>
                                    <div className={classes.videosList}>
                                        {filteredContent(videos, activeCategory).map(video => (
                                            <div key={video.id} className={classes.videoItem}>
                                                <div className={classes.videoThumbnail}>
                                                    <div className={classes.videoIcon}>{video.thumbnail}</div>
                                                    <div className={classes.videoDuration}>{video.duration}</div>
                                                </div>
                                                <div className={classes.videoInfo}>
                                                    <h5 className={classes.videoTitle}>{video.title}</h5>
                                                    <div className={classes.videoViews}>{video.views} views</div>
                                                </div>
                                                <button
                                                    className={classes.playBtn}
                                                    onClick={() => handleWatchVideo(video.id)}
                                                >
                                                    ▶️
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Tools Section */}
                            <div className={classes.toolsSection}>
                                <div className={classes.premiumCard}>
                                    <h3 className={classes.cardTitle}>Financial Tools</h3>
                                    <div className={classes.toolsList}>
                                        {tools.map(tool => (
                                            <div key={tool.id} className={classes.toolItem}>
                                                <div className={classes.toolIcon}>{tool.icon}</div>
                                                <div className={classes.toolInfo}>
                                                    <h5 className={classes.toolName}>{tool.name}</h5>
                                                    <p className={classes.toolDescription}>{tool.description}</p>
                                                </div>
                                                <button
                                                    className={classes.useToolBtn}
                                                    onClick={() => handleUseTool(tool.id)}
                                                >
                                                    Use Tool
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Achievement Section */}
                    <div className={classes.achievementSection}>
                        <div className={classes.achievementCard}>
                            <div className={classes.achievementContent}>
                                <div className={classes.achievementIcon}>🏆</div>
                                <div className={classes.achievementText}>
                                    <h3 className={classes.achievementTitle}>Keep Learning!</h3>
                                    <p className={classes.achievementDescription}>
                                        Complete 2 more courses this month to earn your "Financial Scholar" badge
                                    </p>
                                </div>
                                <div className={classes.achievementProgress}>
                                    <div className={classes.achievementBar}>
                                        <div className={classes.achievementFill} style={{ width: '60%' }}></div>
                                    </div>
                                    <span className={classes.achievementPercent}>3/5 courses</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Learn;